import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { trackAddToCart, trackRemoveFromCart } from '../analytics/track';

const CartContext = createContext(null);
const STORAGE_KEY = 'nooshin_cart';
const CHANNEL_NAME = 'nooshin_cart_sync';

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { restaurantSlug, item } = action.payload;
      const cartKey = restaurantSlug;
      const existing = state[cartKey] || {};
      const currentQty = existing[item.id]?.quantity || 0;
      return {
        ...state,
        [cartKey]: {
          ...existing,
          [item.id]: { ...item, quantity: currentQty + 1 },
        },
      };
    }
    case 'REMOVE_ITEM': {
      const { restaurantSlug, itemId } = action.payload;
      const cartKey = restaurantSlug;
      const existing = { ...(state[cartKey] || {}) };
      const currentQty = existing[itemId]?.quantity || 0;
      if (currentQty <= 1) {
        delete existing[itemId];
      } else {
        existing[itemId] = { ...existing[itemId], quantity: currentQty - 1 };
      }
      return { ...state, [cartKey]: existing };
    }
    case 'SET_QUANTITY': {
      const { restaurantSlug, item, quantity } = action.payload;
      const cartKey = restaurantSlug;
      const existing = { ...(state[cartKey] || {}) };
      if (quantity <= 0) {
        delete existing[item.id];
      } else {
        existing[item.id] = { ...item, quantity };
      }
      return { ...state, [cartKey]: existing };
    }
    case 'CLEAR_CART': {
      const { restaurantSlug } = action.payload;
      const newState = { ...state };
      delete newState[restaurantSlug];
      return newState;
    }
    case 'HYDRATE': {
      return action.payload;
    }
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const channelRef = useRef(null);

  const loadFromStorage = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const [cart, dispatch] = useReducer(cartReducer, {}, loadFromStorage);

  // Persist to localStorage on every change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    // Broadcast to other tabs
    if (channelRef.current) {
      channelRef.current.postMessage({ type: 'CART_UPDATE', payload: cart });
    }
  }, [cart]);

  // Setup BroadcastChannel for cross-tab sync
  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current.onmessage = (e) => {
        if (e.data?.type === 'CART_UPDATE') {
          dispatch({ type: 'HYDRATE', payload: e.data.payload });
        }
      };
    }
    return () => {
      if (channelRef.current) channelRef.current.close();
    };
  }, []);

  const getCartForRestaurant = (slug) => cart[slug] || {};

  const getItemQuantity = (slug, itemId) => {
    return cart[slug]?.[itemId]?.quantity || 0;
  };

  const getCartTotal = (slug) => {
    const items = Object.values(cart[slug] || {});
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const getCartCount = (slug) => {
    const items = Object.values(cart[slug] || {});
    return items.reduce((sum, item) => sum + item.quantity, 0);
  };

  const addItem = (restaurantSlug, item) => {
    dispatch({ type: 'ADD_ITEM', payload: { restaurantSlug, item } });
    trackAddToCart(item, 1);
  };
  const removeItem = (restaurantSlug, itemId) => {
    const existing = cart[restaurantSlug]?.[itemId];
    dispatch({ type: 'REMOVE_ITEM', payload: { restaurantSlug, itemId } });
    if (existing) trackRemoveFromCart(existing, 1);
  };
  const setQuantity = (restaurantSlug, item, quantity) => {
    const prev = cart[restaurantSlug]?.[item.id]?.quantity || 0;
    dispatch({ type: 'SET_QUANTITY', payload: { restaurantSlug, item, quantity } });
    if (quantity > prev)      trackAddToCart(item, quantity - prev);
    else if (quantity < prev) trackRemoveFromCart(item, prev - quantity);
  };
  const clearCart = (restaurantSlug) => dispatch({ type: 'CLEAR_CART', payload: { restaurantSlug } });

  return (
    <CartContext.Provider value={{ cart, addItem, removeItem, setQuantity, clearCart, getCartForRestaurant, getItemQuantity, getCartTotal, getCartCount }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
